import type { Candidate } from '../candidate'
import type { DerivedFacts } from '../facts'
import { RULE_IDS } from '../version'
import { evidenceFor, missingEvidenceFor, watchItemsFor } from './shared'

/**
 * Lean bulk (phase-system.md, rules-engine.md).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  PLANNED WEIGHT GAIN IS NOT FAILURE, AND MUST NEVER BE LABELLED AS ONE.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The mirror of the maintenance trap: the same +0.25%/week that would be off
 * target during fat loss is the objective here. rules-engine.md states it
 * outright — "do not label planned weight gain as failure".
 *
 * The direction of "faster than range" is handled by `positionAgainstRange` in
 * `facts.ts`, which reads the sign of the phase's own configured range. For a
 * gaining phase, above the range is faster; nothing here re-derives that.
 */
export function evaluateLeanBulk(facts: DerivedFacts): Candidate {
  const phase = facts.phase
  if (!phase || phase.ratePosition === undefined) {
    throw new Error('lean bulk rules require a phase and a comparable week')
  }

  if (phase.ratePosition === 'insideRange') return slowGainOnTrack(facts)
  if (phase.ratePosition === 'fasterThanRange') return gainingTooFast(facts)

  /* Below the range. Losing weight during a surplus is a different problem from
     merely gaining slowly: one means the surplus is not there at all. */
  const losing = (facts.weight.weeklyChangePercent ?? 0) < 0
  return losing ? losingDuringSurplus(facts) : stalled(facts)
}

function slowGainOnTrack(facts: DerivedFacts): Candidate {
  const waistControlled = facts.waist.direction !== 'rising'

  return {
    category: 'stayTheCourse',
    primaryAction: waistControlled
      ? 'Keep going exactly as you are. You are gaining at the rate you planned and your waist is holding, which is what a lean bulk should look like.'
      : 'Keep the current intake, but watch your waist. You are gaining at the planned rate, though your waist has moved up with it.',
    supportingActions: [],
    ruleId: RULE_IDS.leanBulkOnTrack,
    family: 'phaseProgress',
    leadingEvidence: waistControlled ? 'weight' : 'waist',
    evidence: evidenceFor(facts),
    watchItems: waistControlled
      ? watchItemsFor(facts)
      : [
          'A waist rising alongside planned gain is the earliest sign the surplus is larger than it needs to be.',
          ...watchItemsFor(facts),
        ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Reduce intake because the scale is going up',
        reason:
          'Gaining weight is the objective of this phase. The rate is inside the range you set, so this is the plan working.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/**
 * Gaining faster than planned.
 *
 * The documented response is one modest reduction in the surplus — which means
 * this proposes eating less, so `reducesIntakeOrActivity` is **true** and
 * D-007's filter refuses it whenever adherence cannot be judged.
 */
function gainingTooFast(facts: DerivedFacts): Candidate {
  return {
    category: 'considerOneSmallAdjustment',
    primaryAction:
      'Bring the surplus down slightly so your gain comes back inside the range you set. Gaining faster than planned adds fat rather than muscle.',
    supportingActions: [],
    ruleId: RULE_IDS.leanBulkTooFast,
    family: 'phaseProgress',
    leadingEvidence: facts.waist.direction === 'rising' ? 'waist' : 'weight',
    evidence: evidenceFor(facts),
    watchItems: [
      'Muscle is gained slowly whatever the surplus. Faster gain past a point is mostly fat.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Carry on and cut later',
        reason:
          'Fat gained during a bulk has to be lost again afterwards, which costs weeks the phase did not plan for.',
      },
    ],
    reducesIntakeOrActivity: true,
  }
}

/**
 * Weight has stalled below the planned rate.
 *
 * The documented response is one small intake **increase**, so this never trips
 * the D-007 filter — it is not a cut.
 */
function stalled(facts: DerivedFacts): Candidate {
  return {
    category: 'considerOneSmallAdjustment',
    primaryAction:
      'Add a little more food. You are gaining more slowly than the range you set, which usually means the surplus is smaller than intended.',
    supportingActions: [],
    ruleId: RULE_IDS.leanBulkStalled,
    family: 'phaseProgress',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: [
      'Give a change two or three weeks before judging it. Appetite and activity often move on their own.',
      ...watchItemsFor(facts),
    ],
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Train harder to force progress',
        reason:
          'A stall during a surplus is usually about intake rather than effort, and adding training on top of an inadequate surplus tends to cost recovery.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}

/** Losing weight during a phase meant to gain it. */
function losingDuringSurplus(facts: DerivedFacts): Candidate {
  return {
    category: 'considerOneSmallAdjustment',
    primaryAction:
      'Eat more. You are losing weight during a phase meant to gain it, so the surplus is not there at all.',
    supportingActions: [],
    ruleId: RULE_IDS.leanBulkLosing,
    family: 'phaseProgress',
    leadingEvidence: 'weight',
    evidence: evidenceFor(facts),
    watchItems: watchItemsFor(facts),
    missingEvidence: missingEvidenceFor(facts),
    rejectedAlternatives: [
      {
        action: 'Treat the loss as useful body recomposition',
        reason:
          'Project Sola does not plan recomposition, and a phase set up to gain weight is not judged as though it were doing something else (D-012).',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}
